import React, { useState, useEffect } from "react";
import { Card, CardContent, Grid, TextField, Select, MenuItem, FormControl, InputLabel, Button, Typography, Box, Chip, Autocomplete } from "@mui/material";
import { FilterList as FilterIcon, Clear as ClearIcon } from "@mui/icons-material";
import api from "../../services/api";

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 10 }, (_, i) => currentYear - i);

const SubjectAllocationFilterBar = ({ teacherFilter, onTeacherChange, subjectPaperFilter, onSubjectPaperChange, classFilter, onClassChange, yearFilter, onYearChange, termFilter, onTermChange, activeFilter, onActiveFilterChange, onClearFilters, hasActiveFilters, totalCount, isMobile }) => {
  const [teachers, setTeachers] = useState([]);
  const [subjectPapers, setSubjectPapers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        setLoading(true);
        const [teachersRes, subjectPapersRes, classesRes, termsRes] = await Promise.all([
          api.get('/report-card/teachers/active'),
          api.get('/report-card/class-stream-papers', { params: { per_page: 1000 } }),
          api.get('/academic/classes', { params: { per_page: 1000 } }),
          api.get('/academic/terms', { params: { per_page: 1000 } }),
        ]);
        
        setTeachers(Array.isArray(teachersRes.data) ? teachersRes.data : []);
        setSubjectPapers(Array.isArray(subjectPapersRes.data?.data) ? subjectPapersRes.data.data : Array.isArray(subjectPapersRes.data) ? subjectPapersRes.data : []);
        setClasses(Array.isArray(classesRes.data?.data) ? classesRes.data.data : Array.isArray(classesRes.data) ? classesRes.data : []);
        setTerms(Array.isArray(termsRes.data?.data) ? termsRes.data.data : Array.isArray(termsRes.data) ? termsRes.data : []);
      } catch (error) {
        console.error('Error fetching filter data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFilterData();
  }, []);

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6" display="flex" alignItems="center"><FilterIcon sx={{ mr: 1 }} color="primary" />Filters</Typography>
          {hasActiveFilters && <Chip label="Filters Active" color="primary" size="small" />}
        </Box>
        <Grid container spacing={2}>
          <Grid item size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
            <Autocomplete
              size="small"
              loading={loading}
              options={teachers}
              getOptionLabel={(t) => t.full_name || `${t.last_name} ${t.first_name}`}
              value={teachers.find(t => t.id === teacherFilter) || null}
              onChange={(_, val) => onTeacherChange(val ? val.id : '')}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              renderInput={(params) => <TextField {...params} label="Teacher" />}
            />
          </Grid>
          <Grid item size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
            <Autocomplete
              size="small"
              loading={loading}
              options={classes}
              getOptionLabel={(c) => c.name || c.class_name || ''}
              value={classes.find(c => c.id === classFilter) || null}
              onChange={(_, val) => onClassChange(val ? val.id : '')}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              renderInput={(params) => <TextField {...params} label="Class" />}
            />
          </Grid>
          <Grid item size={{ lg: 2.5, md: 4, sm: 6, xs: 12 }}>
            <Autocomplete
              size="small"
              loading={loading}
              options={subjectPapers}
              getOptionLabel={(sp) => {
                const className = sp.class?.name || sp.class?.class_name || 'Unknown Class';
                const streamName = sp.stream?.stream_name ? `- ${sp.stream.stream_name}` : '';
                const subjectName = sp.subject_paper?.subject?.subject_name || 'Unknown Subject';
                const paperName = sp.subject_paper?.paper_name || 'Unknown Paper';
                return `${className} ${streamName} - ${subjectName} (${paperName})`;
              }}
              value={subjectPapers.find(sp => sp.id === subjectPaperFilter) || null}
              onChange={(_, val) => onSubjectPaperChange(val ? val.id : '')}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              renderInput={(params) => <TextField {...params} label="Subject Paper" />}
            />
          </Grid>
          <Grid item size={{ lg: 1.5, md: 4, sm: 6, xs: 12 }}>
            <Autocomplete
              size="small"
              options={yearOptions}
              getOptionLabel={(y) => String(y)}
              value={yearFilter ? Number(yearFilter) : null}
              onChange={(_, val) => onYearChange(val ? String(val) : '')}
              renderInput={(params) => <TextField {...params} label="Year" />}
            />
          </Grid>
          <Grid item size={{ lg: 1.8, md: 4, sm: 6, xs: 12 }}>
            <Autocomplete
              size="small"
              loading={loading}
              options={terms}
              getOptionLabel={(t) => t.term_name || t.name || ''}
              value={terms.find(t => t.id === termFilter) || null}
              onChange={(_, val) => onTermChange(val ? val.id : '')}
              isOptionEqualToValue={(opt, val) => opt.id === val.id}
              renderInput={(params) => <TextField {...params} label="Term" />}
            />
          </Grid>
          <Grid item size={{ lg: 1.4, md: 4, sm: 6, xs: 12 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select value={activeFilter} label="Status" onChange={(e) => onActiveFilterChange(e.target.value)}>
                <MenuItem value="">All</MenuItem>
                <MenuItem value="1">Active</MenuItem>
                <MenuItem value="0">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          {hasActiveFilters && (
            <Grid item size={{ lg: 1.8, md: 4, sm: 6, xs: 12 }}>
              <Button variant="outlined" startIcon={<ClearIcon />} onClick={onClearFilters} size="small" fullWidth>Clear Filters</Button>
            </Grid>
          )}
        </Grid>
        <Box mt={2}><Typography variant="body2" color="text.secondary">Total Allocations: {totalCount}</Typography></Box>
      </CardContent>
    </Card>
  );
};

export default SubjectAllocationFilterBar;
